import React, {useEffect, useState} from "react";
import {useParams, useRouteMatch} from "react-router-dom";
import {useDispatch, useSelector} from "react-redux";
import {getGuest, getInboxData} from "redux/actions/guestsActions";
import GuestSection from "./GuestSection";
import ReservationsSection from "./ReservationsSection";
import Divider from "../../../../../connectui/component/display/Divider";
import Container from "connectui/component/layout/Container";
import {useTheme} from "connectui/theme/ThemeProvider";
import NotesSection from "./NotesSection";
import FollowingSection from "./FollowingSection";
import {useFeatureFlags} from "utilities/featureUtil";
import Skeleton from "connectui/component/display/Skeleton";
import MergeCandidatesCard from "ui/component/mergeguests/card/MergeCandidatesCard";
import MergeGuestModal from "ui/component/mergeguests/dialog/MergeGuestModal";
import ButtonIcon from "connectui/component/input/button/ButtonIcon";
import SvgIcon from "connectui/component/display/SvgIcon";
import ICONS from "connectui/icons/iconMap";
import {guestTypeEnum} from "configuration/enums";

const GuestDetailsPanel = ({guestId, fullDetails = false}) => {
  const {theme, isMobile} = useTheme();
  const params = useParams();
  const profileMatch = useRouteMatch("/admin/messages/profile/:guest_id");
  const chatMatch = useRouteMatch("/admin/messages/chat/:guest_id");
  const localGuestId =
    profileMatch?.params?.guest_id ||
    chatMatch?.params?.guest_id ||
    params.guest_id ||
    guestId;

  const dispatch = useDispatch();
  const guests_dict = useSelector((state) => state.defaultReducer.guests_dict);
  const bookings_dict = useSelector(
    (state) => state.defaultReducer.bookings_dict,
  );
  const loadingGuestDetails = useSelector(
    (state) => state.defaultReducer.loading,
  ).guest;
  const flag = useFeatureFlags();
  const [mergeModalOpen, setMergeModalOpen] = useState(false);
  const [mergeConflicts, setMergeConflicts] = useState(null);
  const [primaryMergeCandidate, setPrimaryMergeCandidate] = useState(null);
  const [secondaryMergeCandidate, setSecondaryMergeCandidate] = useState(null);

  useEffect(() => {
    if (!loadingGuestDetails && localGuestId) {
      const g = guests_dict[localGuestId];
      if (!g || !g.metadata) {
        dispatch(
          getGuest(localGuestId, () => {
            dispatch(getInboxData());
          }),
        );
      }
    }
  }, [localGuestId, guests_dict, loadingGuestDetails, dispatch]);

  return (
    <Container sx={{padding: theme.spacing(5, 4)}}>
      <MergeGuestModal
        isOpen={mergeModalOpen}
        onClose={() => setMergeModalOpen(false)}
        mergeConflicts={mergeConflicts}
        onMerge={() => {
          setMergeModalOpen(false);
        }}
        primaryMergeCandidate={primaryMergeCandidate}
        secondaryMergeCandidate={secondaryMergeCandidate}
      />
      {isMobile && (
        <ButtonIcon
          icon={
            <SvgIcon
              component={ICONS["chevron-left"]}
              sx={{
                width: "16px",
                height: "16px",
                color: theme.colors.textPrimary,
              }}
            />
          }
          onClick={(e) => {
            e.stopPropagation();
            window.history.back();
          }}
          sx={{
            backgroundColor: theme.colors.white,
            borderRadius: "50%",
            cursor: "pointer",
            border: "none",
            boxShadow: "none",
            padding: theme.spacing(1.5, 2),
            marginLeft: theme.spacing(-2),
            float: "left",
            "&:hover": {
              backgroundColor: theme.colors.white,
            },
          }}
        />
      )}
      {!guests_dict[localGuestId] ? (
        <>
          <Skeleton width="100px" height="28px" />
          <Skeleton
            width="100%"
            height="150px"
            style={{marginTop: theme.spacing(3)}}
          />
        </>
      ) : (
        <>
          {guests_dict[localGuestId]?.guest_type !== guestTypeEnum.guest && (
            <FollowingSection guestId={localGuestId} />
          )}
          {[guestTypeEnum.guest, guestTypeEnum.owner].includes(
            guests_dict[localGuestId]?.guest_type,
          ) && <ReservationsSection guestId={localGuestId} />}
        </>
      )}
      {flag.mergeGuests &&
        !isMobile &&
        guests_dict[localGuestId]?.merged_into === null &&
        guests_dict[localGuestId]?.merged_from === null && (
          <>
            <MergeCandidatesCard
              guest={guests_dict[localGuestId]}
              bookings={Object.values(
                bookings_dict.guest_id[localGuestId] || {},
              )}
              onMergeConflictsFound={(response) => {
                setMergeConflicts(response.conflicts);
                setMergeModalOpen(true);
                setPrimaryMergeCandidate(response.primary_merge_candidate);
                setSecondaryMergeCandidate(response.secondary_merge_candidate);
              }}
            />
          </>
        )}
      <Divider />
      <NotesSection guestId={localGuestId} />
      <Divider />
      <GuestSection guestId={localGuestId} fullDetails={fullDetails} />
    </Container>
  );
};

export default GuestDetailsPanel;
